import { Encoding } from '@mode-vegas/vegas';
import { Adapter } from '@mode-switch/adapt';
import { Attr, Model, Names, Values } from '@mode-switch/model';
import { DomainPlan, DomainQuery, PivotQuery } from '@mode-switch/query';
import { FlamingoTypes } from '@mode/shared/contract-common';

type ModeRole = FlamingoTypes.ModeRole;

export function buildColorDomainQuery(
  adapter: Adapter<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding
): DomainPlan<ModeRole> | null {
  const { series, properties } = encoding.marks;
  const props = series.length ? series[0].properties : properties;
  const colorField = props.color.values[0];
  if (typeof colorField?.formula === 'string') {
    // Can't do a domain query on Measure Names|Values
    // without including values from the encoding
    switch (colorField.formula) {
      case Names.Key:
      case Values.Key: {
        return null;
      }
    }

    const color = Adapter.Fields.make(adapter, model, viewName, colorField.formula, Attr.Type.Discrete, {
      alias: colorField.id,
    });

    /**
     * Similarly, if the color field is a measure then its likely not being
     * summarized at the level of detail the user would expect. For example,
     * if the color field were binning values:
     *
     * Field:
     *   [Cust. Sentiment]

     * Formula:
     *   if avg(rating) > 8 then 'great'
     *   elseif avg(rating) > 5 then 'good'
     *   elseif avg(rating) > 2 then 'meh'
     *   else 'nope'
     *   end
     *
     * Having [Cust. Sentiment] in the color channel without including
     * any other dimensions wouldn't be useful.. it would be more useful
     * if we included, say, [Sales Rep. Name]. Then we'd have the categorical
     * sentiment value broken down _by rep_, as opposed to all reps
     *
     * ---
     * ---
     *
     * Using a Pivot DomainPlan restores behavior previous to a change in
     * switch where we started throwing hard when measures are encountered
     * in a Table DomainPlan
     */
    return DomainPlan.pivot(
      model,
      viewName,
      DomainQuery.Pivot.make(PivotQuery.Basic.make({}), {
        fields: [color],
      })
    );
  } else {
    return null;
  }
}
